/*
 * Hero — above-image headline fitter
 *
 * Caps the line count of two elements inside .hero-lotus__media-pill:
 *   .hero-lotus__media-pill__line1  -> max 2 lines
 *   .hero-lotus__media-pill__line3  -> max 1 line
 *
 * Strategy: starting from the CSS-defined max font-size, measure the
 * element's rendered scrollHeight against (lineHeight × maxLines). If
 * it overflows, shrink font-size by 5% and re-measure. Loop until it
 * fits or hits a floor. Re-runs on resize and in the theme editor.
 *
 * Why JS and not CSS: clamp()/container queries can size by viewport
 * width, but they can't react to the actual text content, which is
 * what determines whether "Black Friday Sale" wraps to 2
 * or 3 lines on any given screen.
 */
(() => {
  'use strict';

  const ORIGINAL_KEY = 'fitOriginalSize';
  const MIN_LINE1_PX = 18;
  const MIN_LINE3_PX = 10;
  // How far Line 1 is allowed to grow above its CSS-defined size if its
  // content only takes 1 line — keeps shorter copy looking confident
  // without ballooning out of control.
  const LINE1_MAX_GROWTH = 1.6;
  // Line 3 caption gets to grow more — short captions ("When you purchase
  // all 5 wheels") should fill the available width without wrapping.
  const LINE3_MAX_GROWTH = 5;

  const lineCount = (el, lineHeight) =>
    Math.max(1, Math.round(el.scrollHeight / lineHeight));

  /**
   * Shrink to fit. Used by lines that should never exceed N lines (e.g. Line 3
   * capped at 1 line).
   */
  const fitMaxLines = (el, maxLines, minPx) => {
    if (!el || !el.isConnected) return;
    if (!el.dataset[ORIGINAL_KEY]) {
      el.dataset[ORIGINAL_KEY] = parseFloat(getComputedStyle(el).fontSize);
    }
    el.style.fontSize = el.dataset[ORIGINAL_KEY] + 'px';

    requestAnimationFrame(() => {
      let guard = 80;
      while (guard-- > 0) {
        const cs = getComputedStyle(el);
        const lh = parseFloat(cs.lineHeight);
        const size = parseFloat(cs.fontSize);
        if (!lh) break;
        if (el.scrollHeight <= lh * maxLines + 1) break;
        if (size <= minPx) break;
        el.style.fontSize = (size * 0.95) + 'px';
      }
    });
  };

  /**
   * Grow to fill, capped at maxLines. Used by Line 3: the caption should be
   * as big as possible while staying on 1 line. Shrinks if it starts wrapped,
   * then grows step-by-step until one more 1.05× step would wrap; reverts.
   */
  const fillToMaxLines = (el, maxLines, minPx, maxGrowth) => {
    if (!el || !el.isConnected) return;
    if (!el.dataset[ORIGINAL_KEY]) {
      el.dataset[ORIGINAL_KEY] = parseFloat(getComputedStyle(el).fontSize);
    }
    const orig = parseFloat(el.dataset[ORIGINAL_KEY]);
    el.style.fontSize = orig + 'px';

    requestAnimationFrame(() => {
      let cs = getComputedStyle(el);
      let lh = parseFloat(cs.lineHeight);
      if (!lh) return;
      let size = parseFloat(cs.fontSize);
      let lines = lineCount(el, lh);

      let guard = 80;
      // 1) Shrink if currently wraps past maxLines
      while (lines > maxLines && size > minPx && guard-- > 0) {
        size *= 0.95;
        el.style.fontSize = size + 'px';
        cs = getComputedStyle(el);
        lh = parseFloat(cs.lineHeight);
        lines = lineCount(el, lh);
      }

      // 2) Grow until the next step would exceed maxLines (or hit ceiling)
      const ceiling = orig * (maxGrowth || 1);
      while (size < ceiling && guard-- > 0) {
        const prev = size;
        size *= 1.05;
        el.style.fontSize = size + 'px';
        cs = getComputedStyle(el);
        lh = parseFloat(cs.lineHeight);
        lines = lineCount(el, lh);
        if (lines > maxLines) {
          size = prev;
          el.style.fontSize = size + 'px';
          break;
        }
      }
    });
  };

  /**
   * Two-way fit. Used by Line 1: target exactly 2 lines.
   *   - if currently > 2 lines → shrink until it fits in 2
   *   - if currently < 2 lines → grow (up to LINE1_MAX_GROWTH × original)
   *     until it wraps to 2; back off one step if it overshoots to 3.
   */
  const fitToLineRange = (el, minLines, maxLines, minPx, maxGrowth) => {
    if (!el || !el.isConnected) return;
    if (!el.dataset[ORIGINAL_KEY]) {
      el.dataset[ORIGINAL_KEY] = parseFloat(getComputedStyle(el).fontSize);
    }
    const orig = parseFloat(el.dataset[ORIGINAL_KEY]);
    el.style.fontSize = orig + 'px';

    requestAnimationFrame(() => {
      let cs = getComputedStyle(el);
      let lh = parseFloat(cs.lineHeight);
      if (!lh) return;
      let size = parseFloat(cs.fontSize);
      let lines = lineCount(el, lh);

      let guard = 80;
      // Shrink if too many lines
      while (lines > maxLines && size > minPx && guard-- > 0) {
        size *= 0.95;
        el.style.fontSize = size + 'px';
        cs = getComputedStyle(el);
        lh = parseFloat(cs.lineHeight);
        lines = lineCount(el, lh);
      }

      // Grow if too few lines, but cap so short titles don't become billboards
      const ceiling = orig * (maxGrowth || 1);
      while (lines < minLines && size < ceiling && guard-- > 0) {
        size *= 1.05;
        el.style.fontSize = size + 'px';
        cs = getComputedStyle(el);
        lh = parseFloat(cs.lineHeight);
        lines = lineCount(el, lh);
        if (lines > maxLines) {
          // Overshot — back off one step and stop
          size /= 1.05;
          el.style.fontSize = size + 'px';
          break;
        }
      }
    });
  };

  const fitAll = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.hero-lotus__media-pill__line1').forEach((el) =>
      fitToLineRange(el, 2, 2, MIN_LINE1_PX, LINE1_MAX_GROWTH)
    );
    scope.querySelectorAll('.hero-lotus__media-pill__line3').forEach((el) =>
      fillToMaxLines(el, 1, MIN_LINE3_PX, LINE3_MAX_GROWTH)
    );
  };

  const init = () => {
    fitAll(document);

    // Debounced resize handler so we re-fit on viewport changes.
    let resizeTimer = null;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => fitAll(document), 120);
      },
      { passive: true }
    );

    // Theme editor: re-fit when the section is reloaded or a block changes.
    document.addEventListener('shopify:section:load', (e) => fitAll(e.target));
    document.addEventListener('shopify:block:select', (e) => fitAll(e.target));
    document.addEventListener('shopify:section:select', (e) => fitAll(e.target));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
